import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Table } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { endOfWeek, format, isValid, startOfWeek, subDays } from 'date-fns'
import {
  ApprovedLeavesDocument,
  EmployeeOffDaysDocument,
  FavouriteProjectsDocument,
  FavouriteProjectsQuery,
  GlobalTimeRecordsDocument,
  GlobalTimeRecordsQuery,
} from 'generated/resources/graphql'
import { compact } from 'lodash'
import moment from 'moment'

import { useQuery } from 'modules/core/hooks/useQuery'
import { i18nDateToString } from 'modules/core/lib/dates'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { offDayTypesEnum } from 'modules/project_management/lib/constants'
import {
  deleteRepeatedProjects,
  getCustomGlobalTimeRecordsForMyProjectsTable,
  getWeeklyImputedMinutesList,
} from 'modules/project_management/lib/getCustomGlobalTimeRecordsForMyProjectsTable'
import type ProjectsStore from 'modules/project_management/store/models/Projects'

import Body from './_Body'
import Header, { ICurrentWeekHeaderInfo } from './_Header'
import HeaderActions from './_HeaderActions'
import * as styles from './index.vanilla.css'
import { createDateRange, getHalfDayLabelTranslation } from './utils'

interface MyProjectsTableProps {
  projectsStore: ProjectsStore
  hasEmployeeGlobalTimeRecords: (hasGlobalTimeRecords: boolean) => void
  hasGlobalTimeRecords: boolean
  hasTimeOffModule: boolean
  canRegisterFlexibleTracking: boolean
  buildWeekNavigation: (week: Date) => void
  employeeLocationId: number
}

type Range = [Date, Date]
type GlobalTimeRecord =
  GlobalTimeRecordsQuery['projectManagement']['globalTimeRecords']['nodes'][number]
type FavouriteProject =
  FavouriteProjectsQuery['projectManagement']['favouriteProjectsConnection']['nodes'][number]
type customizableProject = GlobalTimeRecord | FavouriteProject

const MyProjectsTable = ({
  projectsStore,
  hasEmployeeGlobalTimeRecords,
  hasGlobalTimeRecords,
  hasTimeOffModule,
  canRegisterFlexibleTracking,
  buildWeekNavigation,
  employeeLocationId,
}: MyProjectsTableProps) => {
  const { employeeId, year, month, day } = useParams()
  const dateString = `${year}-${month}-${day}`
  const isValidDate = isValid(new Date(dateString))

  const currentWeekEnd = endOfWeek(isValidDate ? dateString : new Date())
  const currentWeekStart = startOfWeek(isValidDate ? dateString : new Date())
  const [range, setRange] = useState<Range>([currentWeekStart, currentWeekEnd])
  const [localStorageFilters] = useLocalStorage<string[]>(LOCAL_STORAGE_KEYS.myProjectsFilters)

  const [filterValues, setFilterValues] = useState<string[]>(localStorageFilters || [])
  const [isAddProjectModalOpen, setIsAddProjectModalOpen] = useState<boolean>(false)

  const weekStart = format(range[0], 'yyyy-MM-dd')
  const weekEnd = format(range[1], 'yyyy-MM-dd')
  const lastWeekStart = format(subDays(weekStart, 7), 'yyyy-MM-dd')
  const lastWeekEnd = format(subDays(weekEnd, 7), 'yyyy-MM-dd')
  const shouldFetchLastWeekRecords = filterValues.includes('last_week_projects')
  const { data: globalTimeRecords } = useQuery(GlobalTimeRecordsDocument, {
    variables: {
      startsOn: weekStart,
      endsOn: weekEnd,
      projectWorkerIds: projectsStore.projectWorkerIds,
    },
  })

  const { data: favouriteProjects } = useQuery(FavouriteProjectsDocument, {
    variables: {
      employeeId: Number(employeeId),
    },
  })
  const { data: lastWeekGlobalTimeRecords } = useQuery(GlobalTimeRecordsDocument, {
    skip: !shouldFetchLastWeekRecords,
    variables: {
      startsOn: lastWeekStart,
      endsOn: lastWeekEnd,
      projectWorkerIds: projectsStore.projectWorkerIds,
    },
  })
  const { data: approvedLeaves } = useQuery(ApprovedLeavesDocument, {
    skip: !hasTimeOffModule,
    variables: {
      from: weekStart,
      to: weekEnd,
      employeeId: Number(employeeId),
    },
  })
  const { data: employeeOffDays } = useQuery(EmployeeOffDaysDocument, {
    variables: {
      startAt: weekStart,
      endAt: weekEnd,
      locationId: employeeLocationId,
      employeeId: Number(employeeId),
    },
  })
  const globalTimeRecordsList = globalTimeRecords?.projectManagement.globalTimeRecords.nodes || []
  const favouriteProjectsList =
    favouriteProjects?.projectManagement.favouriteProjectsConnection.nodes || []
  const lastWeekGlobalTimeRecordsList =
    lastWeekGlobalTimeRecords?.projectManagement.globalTimeRecords.nodes || []
  const approvedLeavesList = approvedLeaves?.timeoff.approvedLeaves.nodes || []
  const canReadLeaves = approvedLeaves?.employees.employee?._policies?.readLeaves.can || false
  const companyHolidaysList = employeeOffDays?.holidays.companyHolidays.nodes || []
  const estimatedWorkDaysList = employeeOffDays?.timePlanning.estimatedWorkDays.nodes || []

  const createCustomProject = (
    project: customizableProject,
    favouriteProjectId: number | undefined
  ) => {
    return {
      favouriteProjectId: favouriteProjectId,
      projectId: project.projectWorker?.imputableProject?.id,
      projectName: project.projectWorker?.imputableProject?.name,
      projectStatus: project.projectWorker?.imputableProject?.status || 'active',
      projectWorkerId: project.projectWorker?.id,
      projectAssigned: true,
      subprojectName: project.subproject?.name,
      subprojectId: project.subproject?.id ?? undefined,
      imputedMinutesList: getWeeklyImputedMinutesList(weekStart, 0, null, null, true),
    }
  }

  const customFavouriteProjects = favouriteProjectsList.map((project) =>
    createCustomProject(project, project.id)
  )

  const customLastWeekProjects = deleteRepeatedProjects(
    lastWeekGlobalTimeRecordsList.map((project) => {
      const favouriteProject = customFavouriteProjects.find(
        (fp) =>
          fp.projectWorkerId === project.projectWorker?.id &&
          fp.subprojectId == project.subproject?.id
      )

      return createCustomProject(project, favouriteProject?.favouriteProjectId)
    })
  )
  const approvedLeavesDatesList = approvedLeavesList.flatMap((item) =>
    createDateRange(item.startOn, item.finishOn, item.leaveTypeName, item.halfDay, item.__typename)
  )
  const restDaysList = compact(
    estimatedWorkDaysList.map((item) => {
      if (Number(item.hoursInCents) === 0)
        return {
          date: item.date,
          offDayName: '',
          halfDay: null,
          __typename: item.__typename,
        }
    })
  )

  const currentWeekHeaderInfo = () => {
    const days = []

    for (let i = 0; i < 7; i++) {
      const currentDate = i18nDateToString(moment(weekStart).add(i, 'days'), 'ui_short')
      const matchingDateObjects = [
        ...approvedLeavesDatesList,
        ...companyHolidaysList,
        ...restDaysList,
      ].filter((item) => i18nDateToString(moment(item.date), 'ui_short') === currentDate)

      if (matchingDateObjects.length > 0) {
        const singleDayInfo: ICurrentWeekHeaderInfo = {
          date: '',
          offDayNames: [],
          offDayType: offDayTypesEnum.withoutOffDay,
        }
        matchingDateObjects.forEach((item) => {
          singleDayInfo.date = i18nDateToString(moment(item.date), 'ui_short')
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          singleDayInfo.offDayType = item.__typename as offDayTypesEnum
          if (item.offDayName && singleDayInfo.offDayNames) {
            if (item.halfDay) {
              singleDayInfo.offDayNames.push(
                `${item.offDayName} - ${getHalfDayLabelTranslation(item.halfDay)}`
              )
            } else {
              singleDayInfo.offDayNames.push(item.offDayName)
            }
          }
        })
        days.push(singleDayInfo)
      } else {
        days.push({
          date: currentDate,
          offDayNames: [],
          offDayType: offDayTypesEnum.withoutOffDay,
        })
      }
    }
    return days
  }

  const customGlobalTimeRecords = getCustomGlobalTimeRecordsForMyProjectsTable(
    globalTimeRecordsList,
    customFavouriteProjects,
    customLastWeekProjects,
    filterValues
  )

  return (
    // eslint-disable-next-line factorial/no-raw-html
    <div className={styles.myProjectsTable}>
      <HeaderActions
        buildWeekNavigation={buildWeekNavigation}
        canRegisterFlexibleTracking={canRegisterFlexibleTracking}
        filterValues={filterValues}
        hasGlobalTimeRecords={hasGlobalTimeRecords}
        range={range}
        setFilterValues={setFilterValues}
        setIsAddProjectModalOpen={setIsAddProjectModalOpen}
        setRange={setRange}
      />
      <Table fullWidth overflowY="visible">
        <Header currentWeekHeaderInfo={currentWeekHeaderInfo()} canReadLeaves={canReadLeaves} />
        <Body
          currentWeekHeaderInfo={currentWeekHeaderInfo()}
          projectsStore={projectsStore}
          favouriteProjects={customFavouriteProjects}
          flexibleTimeRecords={customGlobalTimeRecords}
          hasEmployeeGlobalTimeRecords={hasEmployeeGlobalTimeRecords}
          hasFavouriteProjectsSelected={filterValues.includes('favourite_projects')}
          hasLastWeekProjectsSelected={filterValues.includes('last_week_projects')}
          lastWeekProjects={customLastWeekProjects}
          weekStart={weekStart}
          weekEnd={weekEnd}
          canRegisterFlexibleTracking={canRegisterFlexibleTracking}
          isAddProjectModalOpen={isAddProjectModalOpen}
          setIsAddProjectModalOpen={setIsAddProjectModalOpen}
        />
      </Table>
    </div>
  )
}

export default MyProjectsTable
